import React, {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Button, Checkbox, Typography} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';
import {tid} from '../../../../../testUtils';
import {useMounted} from '../../../../../hooks/useMounted';
import {modelDeleteDraft} from '../../../../../store/model';
import {getUrlRoute, MRAppRoutes} from '../../../../router/routes';
import {useNavigate} from 'react-router-dom';
import {messageAdd, MessageTypes} from '../../../../../store/message';
import {ModelActionProps} from '../../../actionMenuTypes';
import {MODEL_ACTION_MENU_IDS} from '../../../../../test/types';

const {Text, Title} = Typography;
const headingTitleStyle = {margin: '8px 0 1rem 0', fontSize: '2.2rem', lineHeight: '1.6rem', fontWeight: 700 as const};
const headingSubtitleStyle = {fontSize: '2rem', lineHeight: '2.6rem', fontWeight: 400 as const};

const ModelDeleteDraftSideBarModal: React.FC<ModelActionProps> = ({onCancel, onSuccess, model}) => {
  const {uuid} = model;
  const dispatch = useDispatch();
  const mounted = useMounted();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [agree, setAgree] = useState(false);

  const handleSubmit = useCallback(() => {
    setSubmitting(true);
    return modelDeleteDraft(dispatch, uuid)
      .then(() => {
        dispatch(messageAdd(`Model has been deleted`, MessageTypes.SUCCESS));
        onSuccess?.();
      })
      .catch(() => null)
      .finally(() => {
        navigate(getUrlRoute(MRAppRoutes.MODEL_LIST));
        mounted.current && setSubmitting(false);
      });
  }, [dispatch, navigate, uuid, mounted, onSuccess]);

  return (
    <>
      <div style={{display: 'flex', gap: '1rem', alignItems: 'flex-start'}}>
        {/* Left: Icon */}
        <div style={{flexShrink: 0}}>
          <DeleteOutlined style={{color: '#ff4d4f', fontSize: '3.2rem'}} />
        </div>

        {/* Right: Content */}
        <div style={{flex: 1}}>
          {/* Title */}
          <Title level={5} style={headingTitleStyle}>
            Delete Model Entry
          </Title>

          {/* Subtitle */}
          <div style={{marginBottom: '1.2rem'}}>
            <Text style={headingSubtitleStyle}>This operation can&apos;t be undone!</Text>
          </div>

          <div style={{marginBottom: '1.6rem', display: 'flex', alignItems: 'flex-start', gap: '8px'}}>
            <Checkbox
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              style={{marginTop: '2px', flexShrink: 0}}
            />
            <span onClick={() => setAgree(!agree)} style={{cursor: 'pointer', userSelect: 'none'}}>
              I confirm that this model will be permanently deleted alongside with all files.
            </span>
          </div>

          <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: '2.4rem', gap: '0.8rem'}}>
            <Button onClick={onCancel} {...tid(MODEL_ACTION_MENU_IDS.DELETE_DRAFT, 'cancel')}>
              Cancel
            </Button>
            <Button
              danger
              type="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!agree}
              icon={<DeleteOutlined />}
              {...tid(MODEL_ACTION_MENU_IDS.DELETE_DRAFT, 'submit')}
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModelDeleteDraftSideBarModal;
